"use strict";
// One derivation of a surplus bucket's planned-vs-spent state, for the dashboard
// bucket cards on web and mobile (BEN-141 P2, see #890).
//
// WHY IT IS SHARED. Both dashboards render the same five-to-n bucket cards off
// the same `/api/forecasting/plan/` payload, and #781 is open about the two
// clients drifting apart on exactly this kind of derivation. `computeStatus` is
// already written twice inside `frontend/` alone (`pages/Budgets.tsx` and
// `components/budgets/PayPeriodView.tsx`); a third and fourth copy, one per
// platform, is how the two dashboards end up disagreeing about whether a bucket
// is over.
//
// WHAT `spent` IS, AND WHAT IT IS NOT. The API sets `spent` from the parent
// category named by `spent_source_parent`, summed over the current pay period
// (`_spent_by_parent` in `apps/forecasting/services/forecast_plan.py`). A bucket
// with no such parent reports `spent: 0.00` permanently, which is why `tracked`
// exists: a progress bar reading "$0 of $400" on a bucket that can never report
// a spend is a false claim, not an empty state.
//
// THE TWO FIGURES ARE NOT SYMMETRIC, and the copy must not pretend they are.
// `planned` (the API's `effective_amount`) is forward-looking: a share of the
// surplus left TODAY. `spent` is backward-looking: what has left the linked
// category since the period began. So the ratio answers "how much of what this
// bucket currently holds has already gone out", which is worth showing, and does
// not answer "am I over budget", which is the Budgets page's question about a
// different object. Nothing here uses the words "budget" or "over budget".
Object.defineProperty(exports, "__esModule", { value: true });
exports.bucketSpendState = bucketSpendState;
/**
 * The threshold at which a bucket stops reading as comfortable. Same 80% the
 * budgets surfaces use, so a user who reads both does not meet two rules.
 */
const APPROACHING_RATIO = 0.8;
const nonNegative = (value) => Number.isFinite(value) && value > 0 ? value : 0;
function bucketSpendState({ spent, effectiveAmount, spentSourceParent, }) {
    const safeSpent = nonNegative(spent);
    const planned = nonNegative(effectiveAmount);
    const tracked = Boolean(spentSourceParent);
    // `planned` is clamped at zero because a bar cannot be negative, and a
    // negative surplus makes `effective_amount` negative. Spending that happened
    // anyway is still "over" — it is money out of a category nothing was set aside
    // for. `unfunded` lets the card say that in words instead of printing a ratio
    // against zero, which would contradict the negative headline figure above it.
    if (planned === 0) {
        const over = safeSpent > 0;
        return {
            tracked,
            unfunded: true,
            spent: safeSpent,
            planned,
            percentUsed: over ? 100 : 0,
            status: over ? "over" : "planned",
            tone: over ? "destructive" : "neutral",
            overBy: over ? safeSpent : 0,
        };
    }
    const ratio = safeSpent / planned;
    if (ratio >= 1) {
        return {
            tracked,
            unfunded: false,
            spent: safeSpent,
            planned,
            percentUsed: 100,
            status: "over",
            tone: "destructive",
            overBy: safeSpent - planned,
        };
    }
    const percentUsed = Math.min(100, Math.round(ratio * 100));
    if (safeSpent === 0) {
        return {
            tracked,
            unfunded: false,
            spent: safeSpent,
            planned,
            percentUsed: 0,
            status: "planned",
            tone: "neutral",
            overBy: 0,
        };
    }
    return {
        tracked,
        unfunded: false,
        spent: safeSpent,
        planned,
        percentUsed,
        status: "partly-spent",
        tone: ratio >= APPROACHING_RATIO ? "warning" : "success",
        overBy: 0,
    };
}
//# sourceMappingURL=bucketSpend.js.map